import React from 'react';
import { Link } from 'react-router-dom';
import {
  Anchor,
  Factory,
  Ship,
  Calculator,
  FileText,
  ShieldCheck,
  ClipboardCheck,
  AlertTriangle,
  CheckCircle,
  ArrowRight,
  Phone,
  MessageSquare,
  HelpCircle,
  Building2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../components/ui/accordion';
import usePageSeo from '../hooks/usePageSeo';
import JsonLd from '../components/JsonLd';
import { breadcrumbSchema, serviceSchema, faqSchema } from '../utils/seoSchemas';
import RelatedServices from '../components/RelatedServices';

const HamriyahFreeZoneServices = () => {
  usePageSeo({
    title: 'Audit, VAT & Corporate Tax Services in Hamriyah Free Zone UAE',
    description:
      'Specialist audit, accounting, VAT, corporate tax and AML compliance support for Hamriyah Free Zone Authority companies in Sharjah, UAE.',
    path: '/hamriyah-free-zone-services'
  });

  const services = [
    {
      icon: ClipboardCheck,
      title: 'Hamriyah Audit Services',
      description:
        'Annual financial statement audits prepared in the format expected by Hamriyah Free Zone Authority for licence renewal and regulatory submissions.',
    },
    {
      icon: Calculator,
      title: 'Accounting & Bookkeeping',
      description:
        'Cloud bookkeeping (Zoho / QuickBooks) tailored to trading, manufacturing and logistics businesses operating from Hamriyah Free Zone.',
    },
    {
      icon: FileText,
      title: 'Corporate Tax Compliance',
      description:
        'Corporate Tax registration, Qualifying Free Zone Person assessment, Small Business Relief evaluation and annual CT return filing.',
    },
    {
      icon: ShieldCheck,
      title: 'VAT Registration & Filing',
      description:
        'VAT registration for traders, import / export businesses and service providers — plus quarterly VAT return filing and FTA correspondence.',
    },
    {
      icon: AlertTriangle,
      title: 'AML / goAML Support',
      description:
        'AML risk assessments and goAML registration for DNFBPs operating from Hamriyah — including precious metals traders, real estate brokers and corporate service providers.',
    },
    {
      icon: Building2,
      title: 'Advisory & Renewals',
      description:
        'Practical advisory across financial reporting, internal controls and audit support during your Hamriyah Free Zone licence renewal cycle.',
    }
  ];

  // Hamriyah-specific challenges (DIFFERENT from SAIF to avoid duplicate content)
  const challenges = [
    {
      title: 'Trading & Manufacturing Bookkeeping',
      description: 'Hamriyah is dominated by traders, manufacturers and oil & gas suppliers — inventory accounting, cost-of-goods workflows and multi-currency transactions need disciplined recording.'
    },
    {
      title: 'Annual Audit for Licence Renewal',
      description: 'Hamriyah Free Zone Authority commonly requests audited financial statements at renewal. Delays here can hold up your licence.'
    },
    {
      title: 'Corporate Tax & QFZP Assessment',
      description: 'Most Hamriyah entities must register for Corporate Tax and decide whether they qualify for the 0% Qualifying Free Zone Person regime — a technical assessment.'
    },
    {
      title: 'VAT on Import / Export & Designated Zone Rules',
      description: 'Hamriyah is partly treated as a VAT Designated Zone for goods. Misapplying reverse charge or zero-rating rules is a common FTA finding.'
    },
    {
      title: 'ESR & AML Documentation Gaps',
      description: 'Trading and holding entities can fall under Economic Substance Regulations and AML obligations — documentation often lags behind operations.'
    }
  ];

  const sectors = [
    { name: 'Trading & Distribution', icon: Ship },
    { name: 'Oil, Gas & Petrochemicals', icon: Factory },
    { name: 'Manufacturing & Industrial', icon: Factory },
    { name: 'Logistics & Shipping', icon: Anchor },
    { name: 'Steel & Heavy Industries', icon: Factory },
    { name: 'Service & Consulting Firms', icon: Building2 }
  ];

  const whyChoose = [
    'Hands-on experience with Hamriyah Free Zone Authority submissions',
    'Audit reports in the format expected at licence renewal',
    'Specialists in trading, manufacturing and logistics accounting',
    'Joined-up VAT, Corporate Tax and AML compliance',
    'Direct partner-level engagement — not handed to junior staff',
    'Transparent fixed-fee packages for SMEs'
  ];

  // Hamriyah-specific FAQs
  const faqs = [
    {
      q: 'Is audit mandatory in Hamriyah Free Zone?',
      a: 'Hamriyah Free Zone Authority generally expects audited financial statements at licence renewal, especially for trading, industrial and service companies. The exact requirement depends on your licence category and activity. WPAA can confirm your obligation and prepare the audit in the format the authority expects.'
    },
    {
      q: 'When should I start maintaining proper books for my Hamriyah company?',
      a: 'From day one. UAE Corporate Tax and VAT laws require proper accounting records to be maintained and retained for at least 5 years. Starting late means a costly catch-up before audit and tax season.'
    },
    {
      q: 'Does Corporate Tax apply to Hamriyah Free Zone companies?',
      a: 'Yes. Hamriyah Free Zone entities are within the scope of UAE Corporate Tax and must register with the FTA. Whether you pay 0% (as a Qualifying Free Zone Person) or 9% depends on your income sources and substance — we assess this for you.'
    },
    {
      q: 'Is Hamriyah considered a VAT Designated Zone?',
      a: 'Parts of Hamriyah Free Zone are treated as a Designated Zone for VAT purposes on goods, which changes how supplies are taxed. Services are generally treated as supplied in the UAE mainland. WPAA reviews your transaction flows to apply the correct VAT treatment.'
    },
    {
      q: 'Can you handle accounting backlog and clean-up before an audit?',
      a: 'Yes. Backlog clean-up is one of the most common engagements for Hamriyah-based SMEs. We rebuild books on Zoho or QuickBooks, reconcile bank and inventory positions, and prepare audit-ready financials.'
    },
    {
      q: 'Do you support DNFBP / goAML registration for Hamriyah entities?',
      a: 'Yes. We assist precious metal traders, real estate brokers, corporate service providers and other DNFBPs operating from Hamriyah with goAML registration, AML policies and ongoing compliance documentation.'
    }
  ];

  return (
    <div className="min-h-screen" data-testid="hamriyah-page">
      <JsonLd
        id="ld-hfz-service"
        data={serviceSchema({
          name: 'Hamriyah Free Zone Audit, VAT & Corporate Tax Services',
          description:
            'Audit, accounting, VAT, Corporate Tax and AML / goAML compliance services for Hamriyah Free Zone Authority companies in Sharjah, UAE.',
          path: '/hamriyah-free-zone-services',
          serviceType: 'Free Zone Compliance Services - Hamriyah'
        })}
      />
      <JsonLd
        id="ld-hfz-breadcrumb"
        data={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'UAE Free Zone Services', path: '/free-zone-services-uae' },
          { name: 'Hamriyah Free Zone', path: '/hamriyah-free-zone-services' }
        ])}
      />
      <JsonLd id="ld-hfz-faq" data={faqSchema(faqs)} />

      {/* HERO */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-slate-900 via-emerald-900 to-teal-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-400 rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-teal-400 rounded-full filter blur-3xl"></div>
        </div>
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center space-x-2 mb-6">
              <Anchor className="text-emerald-400" size={32} />
              <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">
                Hamriyah Free Zone — Sharjah
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Audit, VAT & Corporate Tax Services in Hamriyah Free Zone
            </h1>
            <p className="text-2xl text-emerald-200 mb-6 font-semibold">
              Compliance Support for Hamriyah Free Zone Companies
            </p>
            <p className="text-lg text-gray-300 mb-8 leading-relaxed max-w-3xl">
              From trading houses and manufacturers to logistics and service firms, WPAA delivers practical audit,
              accounting, VAT, Corporate Tax and AML support for businesses licensed by Hamriyah Free Zone Authority.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact?service=hamriyah-free-zone">
                <Button size="lg" className="bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-6 text-lg" data-testid="hfz-consultation-btn">
                  Book Free Consultation
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" data-testid="hfz-whatsapp-btn">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                  WhatsApp Us
                </Button>
              </a>
              <a href="tel:+971501219341" data-testid="hfz-call-btn">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                  Call Now
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* AUTHORITY CONTEXT */}
      <section className="py-16 px-4 bg-white border-b border-gray-100">
        <div className="container mx-auto max-w-5xl">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">About the Authority</span>
              <h2 className="text-3xl font-bold text-gray-900 mt-3 mb-4">Hamriyah Free Zone Authority</h2>
              <p className="text-gray-600 leading-relaxed mb-3">
                Established in Sharjah, Hamriyah Free Zone Authority (HFZA) is one of the UAE&apos;s largest industrial
                free zones — home to thousands of trading, manufacturing, oil &amp; gas and logistics companies. Its
                deep-water port access and industrial land make it a hub for heavy industry and import / export
                operations.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Because of this business mix, HFZA entities face specific compliance pressures around inventory
                accounting, VAT Designated Zone rules, Corporate Tax substance, ESR and audit-at-renewal.
              </p>
            </div>
            <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-100">
              <h3 className="font-bold text-gray-900 mb-3">Typical HFZA Compliance Cycle</h3>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex gap-2"><CheckCircle size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" /> Monthly bookkeeping</li>
                <li className="flex gap-2"><CheckCircle size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" /> Quarterly VAT filing</li>
                <li className="flex gap-2"><CheckCircle size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" /> Annual audited financials</li>
                <li className="flex gap-2"><CheckCircle size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" /> Annual Corporate Tax filing</li>
                <li className="flex gap-2"><CheckCircle size={16} className="text-emerald-600 mt-0.5 flex-shrink-0" /> ESR / AML notifications where applicable</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Services</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-4">What WPAA Delivers in Hamriyah</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc, idx) => {
              const Icon = svc.icon;
              return (
                <Card key={idx} className="border-2 hover:border-emerald-500 transition-all hover:shadow-lg">
                  <CardContent className="p-6">
                    <div className="bg-emerald-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="text-emerald-600" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{svc.title}</h3>
                    <p className="text-gray-600">{svc.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CHALLENGES */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Common Pain Points</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-4">
              Where Hamriyah Companies Need Help
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              The compliance issues we encounter most frequently with Hamriyah Free Zone clients.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {challenges.map((c, idx) => (
              <Card key={idx} className="border border-gray-100 hover:shadow-md transition-all">
                <CardContent className="p-6 flex gap-4">
                  <AlertTriangle className="text-amber-500 flex-shrink-0 mt-1" size={22} />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">{c.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{c.description}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SECTORS */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Sectors</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-4">Sectors We Serve in Hamriyah</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {sectors.map((s, idx) => {
              const Icon = s.icon;
              return (
                <div key={idx} className="bg-white rounded-xl p-5 text-center border border-gray-100 hover:border-emerald-500 transition-all">
                  <Icon className="text-emerald-600 mx-auto mb-2" size={28} />
                  <p className="font-semibold text-gray-800 text-sm">{s.name}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY WPAA */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Why WPAA</span>
              <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">
                Why Hamriyah Companies Choose WPAA
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                A Sharjah-based audit and advisory firm that understands the realities of operating from Hamriyah
                Free Zone — and the specific compliance expectations of its authority.
              </p>
            </div>
            <div className="space-y-3">
              {whyChoose.map((w, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <CheckCircle className="text-emerald-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">{w}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">FAQs</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Hamriyah Free Zone — FAQs</h2>
          </div>

          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-white border border-gray-200 rounded-xl px-6 shadow-sm"
                >
                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-emerald-600 hover:no-underline">
                    <div className="flex items-start gap-3">
                      <HelpCircle className="text-emerald-600 flex-shrink-0 mt-1" size={20} />
                      <span>{faq.q}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 text-base leading-relaxed pl-8">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <RelatedServices
        services={[
          { title: 'UAE Free Zone Services', description: 'Master page covering audit, tax and compliance across all UAE free zones.', path: '/free-zone-services-uae', accent: 'teal' },
          { title: 'SAIF Zone Services', description: 'Audit, accounting & compliance for SAIF Zone (Sharjah Airport International Free Zone) entities.', path: '/saif-zone-services', accent: 'emerald' },
          { title: 'Audit Services in UAE', description: 'Statutory and free zone audit services across the UAE.', path: '/audit-services-uae', accent: 'blue' }
        ]}
      />

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-emerald-700 to-teal-700 text-white">
        <div className="container mx-auto text-center max-w-4xl">
          <h2 className="text-4xl font-bold mb-6">Talk to a Hamriyah Free Zone Specialist</h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
            Book a free consultation and we&apos;ll review your audit, VAT and Corporate Tax position in plain English.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
            <Link to="/contact?service=hamriyah-free-zone">
              <Button size="lg" className="bg-white text-emerald-700 hover:bg-gray-100 px-8 py-6 text-lg" data-testid="hfz-cta-consultation-btn">
                Schedule Consultation
              </Button>
            </Link>
            <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" data-testid="hfz-cta-whatsapp-btn">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                WhatsApp Us
              </Button>
            </a>
          </div>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 text-emerald-100">
            <a href="tel:+971509987431" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="hfz-cta-phone-1">
              <Phone size={18} />
              <span>+971 50 998 7431</span>
            </a>
            <a href="https://wa.me/971501219341" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white transition-colors" data-testid="hfz-cta-phone-2">
              <MessageSquare size={18} />
              <span>WhatsApp +971 50 121 9341</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HamriyahFreeZoneServices;
